// Recipient side, /s/:token. No login. Shows only what the server allows
// at each state; the instructions appear only after /reveal/ succeeds.
// Verify flow: (named email link) email code -> mobile number -> TrustStamp
// texts the recipient -> this page polls /verify/status/ -> reveal.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Input } from '@/components/form/fields/Input/Input';
import { PhoneInput } from '@/components/form/fields/PhoneInput/PhoneInput';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { prepareResponse, getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { openSecureLinkApi } from '@/api/v1/secure-links/openSecureLinkApi';
import { revealSecureLinkApi } from '@/api/v1/secure-links/revealSecureLinkApi';
import { secureLinkEmailOtpApi } from '@/api/v1/secure-links/secureLinkEmailOtpApi';
import { startSecureLinkVerificationApi } from '@/api/v1/secure-links/startSecureLinkVerificationApi';
import { getSecureLinkVerificationStatusApi } from '@/api/v1/secure-links/getSecureLinkVerificationStatusApi';
import { RevealedInstructionsType } from '@/types/myVaultTypes';
import { InstructionRow, SecureLinkCard, SecureLinkShell } from './styles';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { ExternalLink } from '@/components/ExternalLink/ExternalLink';
import { TRUST_STAMP_URL } from '@/bundle/_Recipient/_Details/ui/RecipientDocumentVerification/helpers';
import { copyToClipboard } from '@/helpers/helpers';
import { VerificationInProgress } from './VerificationInProgress';
import { ID_VERIFICATION_POLLING_INTERVAL, isIdVerificationLiveStatus } from '@/helpers/verificationHelpers';
import { resendSecureLinkVerificationApi } from '@/api/v1/secure-links/resendSecureLinkVerificationApi';
import { SingleViewNotice } from './SingleViewNotice';
import { Mono } from '@/styles/common';

const DEAD_MESSAGE = {
  expired: 'This link has expired. Ask the sender for a new one.',
  revoked: 'This link was revoked by the sender.',
  completed: 'This one-time link was already viewed.',
};

export const SecureLinkPage = () => {
  const { token } = useParams();
  const [data, setData] = useState<RevealedInstructionsType | null>(null);
  const [emailCode, setEmailCode] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  const { data: openResponse, isPending } = useQuery({
    queryKey: ['secure_link_open', token],
    queryFn: async () => prepareResponse(await openSecureLinkApi(token)),
    retry: false,
  });
  const state = openResponse?.body;

  const { data: statusResponse, refetch: refetchStatus } = useQuery({
    queryKey: ['secure_link_verify_status', token],
    queryFn: async () => prepareResponse(await getSecureLinkVerificationStatusApi(token)),
    enabled: !!state?.requires_verification,
    // Polls while TrustStamp has a live session, on first load as well as after a reload.
    refetchInterval: (query) =>
      isIdVerificationLiveStatus(query.state.data?.body?.status) ? ID_VERIFICATION_POLLING_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
  const verifyStatus = statusResponse?.body;

  const reveal = useMutation({
    mutationKey: ['secure_link_reveal', token],
    mutationFn: async () => prepareResponse(await revealSecureLinkApi(token)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setData(response.body);
    },
  });

  const resend = useMutation({
    mutationKey: ['secure_link_verify_resend', token],
    mutationFn: async () => prepareResponse(await resendSecureLinkVerificationApi(token)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      showSnackbar('We sent a new verification link to your phone.');
      refetchStatus();
    },
  });

  const emailOtp = useMutation({
    mutationKey: ['secure_link_email_otp', token],
    mutationFn: async (code?: string) => prepareResponse(await secureLinkEmailOtpApi(token, code)),
    onSuccess(response, code) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      if (code) setEmailChecked(true);
      else {
        setCodeSent(true);
        showSnackbar('We emailed you a 6-digit code.');
      }
    },
  });

  const start = useMutation({
    mutationKey: ['secure_link_verify_start', token],
    mutationFn: async () => prepareResponse(await startSecureLinkVerificationApi(token, phone)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));

      setError('');
      setShowPhoneForm(false);
      refetchStatus();
    },
  });

  useEffect(() => {
    if (verifyStatus?.status === 'succeed' && !data && !reveal.isPending) {
      reveal.mutate();
    }

    if (verifyStatus?.status === 'failed') {
      setError('Verification did not pass. You can try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyStatus?.status]);

  const copyAll = (dataList: RevealedInstructionsType['fields']) => {
    const copyData = dataList.map((f) => `${f.label}: ${f.value || '-'}`).join('\n');

    copyToClipboard(copyData);
  };

  if (isPending) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography>Loading...</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  if (!state || openResponse?.error) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography variant='headline5'>This link cannot be opened.</Typography>
          <Typography variant='bodySmall'>{getResponseError(openResponse?.error) || 'The link is invalid.'}</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  const dead = DEAD_MESSAGE[state.state];
  const needsEmailCode = state.named_only && state.named_channel === 'email' && !emailChecked;
  const inProgress = isIdVerificationLiveStatus(verifyStatus?.status);
  const resendIn = verifyStatus?.start_new_session_enable_after_sec ?? 0;

  return (
    <SecureLinkShell>
      <SecureLinkCard>
        <Typography variant='headline5' fontWeight='semibold'>
          WireVault secure link
        </Typography>

        {data ? (
          <>
            <SingleViewNotice access={data.access} />
            <Typography>{data.rail_label} instructions</Typography>
            <Typography variant='bodySmall'>
              Shared by <b>{data.shared_by}</b>. Bank: <b>{data.bank_name}</b>. Confirmed by the account holder.
            </Typography>
            <Box>
              {data.fields.map((field) => (
                <InstructionRow key={field.key}>
                  <span>{field.label}</span>
                  <Mono>{field.value || '-'}</Mono>
                  <ButtonNew size='medium' variant='secondary' onClick={() => copyToClipboard(field.value)}>
                    Copy
                  </ButtonNew>
                </InstructionRow>
              ))}
            </Box>
            <Box marginLeft={'auto'}>
              <ButtonNew variant='secondary' onClick={() => copyAll(data.fields)}>
                Copy All
              </ButtonNew>
            </Box>

            <Typography variant='caption'>
              Record {data.record.ref_id} v{data.record.version} · saved{' '}
              {formatDate(data.record.saved_at, DATE_TIME_FORMAT)} · hash <Mono>{data.record.hash.slice(0, 8)}</Mono>.
              Your view was logged. If you receive different instructions by email or phone, do not use them.
            </Typography>
          </>
        ) : dead ? (
          <>
            <Typography variant='headline4'>Link unavailable</Typography>
            <Typography variant='bodySmall'>{dead}</Typography>
          </>
        ) : state.requires_verification ? (
          <>
            <Typography variant='headline4'>Verify your identity</Typography>
            <Typography variant='bodySmall'>
              <b>{state.organization}</b> protects its bank details. Verify once with a government ID and a short
              liveness check through <ExternalLink href={TRUST_STAMP_URL}>TrustStamp</ExternalLink>, then the{' '}
              {state.rail_label} instructions open.
            </Typography>

            {error && <Notification variant='error'>{error}</Notification>}

            {needsEmailCode && (
              <Box display='flex' flexDirection='column' rowGap='12px'>
                <Typography variant='bodySmall'>First, confirm the email address this link was sent to.</Typography>
                {codeSent ? (
                  <>
                    <Input
                      name='code'
                      label='6-digit code'
                      value={emailCode}
                      placeholder='Enter OTP code'
                      onChange={(event) => setEmailCode(event.target.value)}
                    />
                    <Button size='medium' isLoading={emailOtp.isPending} onClick={() => emailOtp.mutate(emailCode)}>
                      Confirm code
                    </Button>
                  </>
                ) : (
                  <Button size='medium' isLoading={emailOtp.isPending} onClick={() => emailOtp.mutate(undefined)}>
                    Email me a code
                  </Button>
                )}
              </Box>
            )}

            {!needsEmailCode && (!inProgress || showPhoneForm) && (
              <Box display='flex' flexDirection='column' rowGap='12px'>
                <PhoneInput name='mobile_phone' label='Your mobile number' value={phone} onChange={setPhone} />
                <Typography variant='caption'>
                  TrustStamp will text you a link to complete the verification on your phone.
                </Typography>
                <Button size='medium' isLoading={start.isPending} disabled={!phone} onClick={() => start.mutate()}>
                  Start verification
                </Button>
              </Box>
            )}

            {!needsEmailCode && inProgress && (
              <VerificationInProgress
                key={verifyStatus.session_id ?? 'session'}
                phone={verifyStatus.phone_masked}
                isLoading={resend.isPending}
                canResend={verifyStatus.can_resend}
                onResend={() => resend.mutate()}
                onChangeNumber={() => setShowPhoneForm(true)}
                resendIn={resendIn}
                onTimerEnds={refetchStatus}
              />
            )}

            <Typography variant='caption' fontWeight='medium' textAlign='right'>
              Expires {formatDate(state.expires_at, DATE_TIME_FORMAT)}
            </Typography>
          </>
        ) : (
          <>
            <Typography>
              <b>{state.organization}</b> shared payment instructions with you
            </Typography>
            <Typography variant='bodySmall'>No verification is required on this link. Opening it is logged.</Typography>
            {error && <Notification variant='error'>{error}</Notification>}
            <Box display='flex' gap='12px' flexDirection='column'>
              <ButtonNew size='large' isLoading={reveal.isPending} onClick={() => reveal.mutate()}>
                View {state.rail_label} instructions
              </ButtonNew>
              <Typography variant='caption' fontWeight='semibold' textAlign='right'>
                Expires {formatDate(state.expires_at, DATE_TIME_FORMAT)}
              </Typography>
            </Box>
          </>
        )}
      </SecureLinkCard>
    </SecureLinkShell>
  );
};
